import React from "react";
import { Button, Tooltip, Stack } from "@mui/material";
import DrawIcon from "@mui/icons-material/Draw";
import CloseIcon from "@mui/icons-material/Close";
import { useLiveAnnotation } from "../../../../hooks/use-live-annotation";

interface ActivateLiveAnnotationButtonProps {
  expired: boolean;
  urlIndex: number;
  showText?: boolean;
  disabled?: boolean;
  liveAnnotationChanged: (showAnnotation: boolean) => void;
  closeToShowAnnotation: () => void;
}

/**
 * Button component for activating/deactivating live annotation in modals
 * Renders as a regular Material-UI Button with text and icons
 */
const ActivateLiveAnnotationButton: React.FC<ActivateLiveAnnotationButtonProps> = ({
  expired,
  urlIndex,
  showText = true,
  disabled,
  liveAnnotationChanged,
  closeToShowAnnotation,
}) => {
  const {
    showAnnotation,
    onClick,
    isDisabled,
    tooltipText,
    label,
    color,
  } = useLiveAnnotation({
    expired,
    urlIndex,
    disabled,
    liveAnnotationChanged,
  });

  return (
    <Stack>
      {!showAnnotation &&
        <Tooltip title={tooltipText}>
          <Button
            onClick={onClick}
            disabled={isDisabled}
            startIcon={showAnnotation ? <CloseIcon sx={{ color: "white" }} /> : <DrawIcon />}
            variant="contained"
            color={color}
            sx={{
              borderRadius: "1.5rem",
              px: 2,
              py: 1,
              height: "40px",
              fontWeight: 600,
              ...(color !== "primary" && {
                backgroundColor: "#c62828",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                },
                color: "white",
              }),
              zIndex: 11,
            }}
          >
            {showText ? label : ""}
          </Button>
        </Tooltip >
      }
      {showAnnotation &&
        <Button
          onClick={closeToShowAnnotation}
          variant="outlined"
          startIcon={<DrawIcon />}
          sx={{
            backgroundColor: "rgba(244, 67, 54, 0.1)",
            borderColor: "#f44336",
            color: "#f44336",
            fontSize: "0.875rem",
            textTransform: "none",
            padding: "8px 16px",
            '&:hover': {
              backgroundColor: "rgba(244, 67, 54, 0.2)",
              borderColor: "#d32f2f",
            },
            zIndex: 11,
          }}
        >
          Live annotation taking place in the background, click here to view it
        </Button>
      }
    </Stack>
  );
};

export default ActivateLiveAnnotationButton;
